import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { gql, useQuery } from "@apollo/client";
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  RadioGroup,
  FormControlLabel,
  Radio,
  Stack,
  Chip,
  Grid,
  useTheme,
  FormControl,
  AppBar,
  Toolbar,
  Select,
  MenuItem,
  InputLabel,
} from "@mui/material";
import LoadingPage from "./LoadingPage";
import ErrorPage from "./ErrorPage";
import { ingredientTasksMap, validateTaskType } from "../constants";
import nutripot from "../Assets/Modules/nutripot.png";
import Veg from "../Assets/FoodType/veg.png";
import NonVeg from "../Assets/FoodType/non-veg.png";
import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  ArrowBack,
} from "@mui/icons-material";
import { keyframes } from "@mui/system";
import Preloader from "../Components/Preloader/Preloader";
import { generateCode } from "../Static/code-generator";
import BreadcrumbsNav from "../Components/Buttons/Breadcrumbs";
import { servingPersons } from "../Static/inventory";
import NoPermissionDialog from "../Components/Dialogs/NoPermissionDialog";
import CookLaterDialog from "../Components/Dialogs/CookLaterDialog";
import { useGetMe } from "../Context/GetMeContext";
import { useKitchenDetail } from "../Context/GetMyKitchenDetailContext";
import { useNotification } from "../Context/notificationContext";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import { mergeRecipes } from "../Validation/MergeTasks";
import TaskRow from "../Components/CookPageTaskRow";
import BreadCrumb from "../Components/BreadCrumb";

const GET_BOXES = gql`
  query GetBoxes(
    $kitchen_id: ID!
    $user_id: ID!
    $inventory_type: InventoryType!
  ) {
    getBoxes(
      kitchen_id: $kitchen_id
      user_id: $user_id
      inventory_type: $inventory_type
    ) {
      id
      inventory_type
      box_no
      item_name
      box_capacity
      box_filled
      box_unit
      created
      modified
    }
  }
`;

export default function CookRecipe() {
  const theme = useTheme();
  const { recipe_id } = useParams();
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const { user } = useGetMe();
  const userdata = user?.me;
  const { kitchenDetail } = useKitchenDetail();

  const [tasks, setTasks] = useState([]);
  const [stationSelected, setStationSelected] = useState(
    localStorage.getItem("stationSelected") || "np1"
  );
  const [isReadyToCook, setIsReadyToCook] = useState(false);
  const [notallowedAlert, setNotallowedAlert] = useState(false);
  const [preloader, setPreloader] = useState(true);
  const [openScheduleModal, setOpenScheduleModal] = useState(false);
  const [scheduledDate, setScheduledDate] = useState(null);
  const [servingCount, setServingCount] = useState(4);
  const [prevKitchenTask, setPrevKitchenTask] = useState(
    () => JSON.parse(localStorage.getItem("kitchenTask")) || null
  );

  const { data: getBoxesData } = useQuery(GET_BOXES, {
    variables: {
      kitchen_id: kitchenDetail?.kitchenId,
      user_id: userdata?.id,
      inventory_type: "ALL",
    },
    skip: !kitchenDetail?.kitchenId || !userdata?.id,
    fetchPolicy: "cache-and-network",
  });

  const inventoryData = useMemo(() => getBoxesData?.getBoxes, [getBoxesData]);

  useEffect(() => {
    const timer = setTimeout(() => setPreloader(false), 1000);
    return () => clearTimeout(timer);
  }, []);

  const checkIfReadyToCook = useCallback((tasks) => {
    const allTasksOk = tasks
      ?.filter((task) => ingredientTasksMap[task.name])
      .every(
        (task) =>
          task.isOk &&
          task.cookIn &&
          (task.cookIn === "np1" || task.cookIn === "np2")
      );
    setIsReadyToCook(allTasksOk);
  }, []);

  useEffect(() => {
    if (tasks?.length > 0) {
      const updatedTasks = tasks.map((task) => ({
        ...task,
        cookIn: stationSelected,
      }));
      setTasks(updatedTasks);
      checkIfReadyToCook(updatedTasks);
    }
  }, [stationSelected, tasks, checkIfReadyToCook]);

  const { data, loading, error } = useQuery(GET_RECIPE_ACTIONS_QUERY, {
    variables: { recipeId: recipe_id },
    skip: !recipe_id,
  });

  const matchInventoryWithTasks = useCallback(
    (fetchedTasks) => {
      if (inventoryData?.length > 0) {
        return fetchedTasks.map((task) => {
          const taskType = validateTaskType(task.type);
          const inventoryItem = inventoryData?.find((inv) => {
            return (
              inv.item_name === task.item_name &&
              taskType?.toLowerCase() === inv.inventory_type?.toLowerCase()
            );
          });

          if (inventoryItem) {
            const isValidQuantityAvailable =
              inventoryItem?.box_filled >= task?.quantity;
            return {
              ...task,
              position: inventoryItem.box_no,
              isOk: isValidQuantityAvailable,
              errorMessage:
                !isValidQuantityAvailable &&
                "Inventory level is low, please refill",
            };
          } else {
            return { ...task, isOk: false };
          }
        });
      }
    },
    [inventoryData]
  );

  useEffect(() => {
    if (data && data.getRecipeActions) {
      const fetchedTasks = JSON.parse(
        data.getRecipeActions.recipe_tasks || "[]"
      );
      const matchedTasks = matchInventoryWithTasks(fetchedTasks);
      setTasks(matchedTasks);
      checkIfReadyToCook(matchedTasks);
    }
  }, [data, checkIfReadyToCook, matchInventoryWithTasks]);

  const handleStationSelect = useCallback(
    (selectedStation) => {
      setStationSelected(selectedStation);
      localStorage.setItem("stationSelected", selectedStation);
      const updatedTasks = tasks.map((task) => ({
        ...task,
        cookIn: selectedStation,
      }));
      setTasks(updatedTasks);
      checkIfReadyToCook(updatedTasks);
    },
    [tasks, checkIfReadyToCook]
  );

  const handleManualEntry = useCallback(
    (taskId, newBoxNo) => {
      const updatedTasks = tasks.map((task) =>
        task.id === taskId ? { ...task, position: newBoxNo, isOk: true } : task
      );
      setTasks(updatedTasks);
      checkIfReadyToCook(updatedTasks);
    },
    [tasks, checkIfReadyToCook]
  );

  const handleNotAllowedAlertClose = useCallback(() => {
    setNotallowedAlert(false);
    setTimeout(() => navigate("/dashboard"), 2000);
  }, [navigate]);

  const handleAddAnotherRecipe = useCallback(() => {
    if (prevKitchenTask) return;
    const kitchenTask = {
      recipe: data?.getRecipeActions,
      tasks,
    };

    localStorage.setItem("kitchenTask", JSON.stringify(kitchenTask));
    navigate("/my-recipes");
    showNotification(
      "warning",
      "Kindly add another recipe so we can cook together and enjoy the experience!"
    );
  }, [data, tasks, prevKitchenTask, navigate, showNotification]);

  const removePreKitechenTasks = useCallback(() => {
    if (prevKitchenTask) {
      localStorage.removeItem("kitchenTask");
      setPrevKitchenTask(null);
      showNotification("success", "Previous recipe tasks removed");
    }
  }, [prevKitchenTask, showNotification]);

  const handleCook = useCallback(async () => {
    if (!isReadyToCook) return;
    if (prevKitchenTask) {
      const prevTask = prevKitchenTask?.tasks[0];
      if (prevTask?.cookIn === stationSelected) {
        showNotification(
          "warning",
          `You have already selected Nutripot ${stationSelected.toUpperCase()} for previous recipe. Please select another Nutripot.`
        );
        return;
      }
    }

    let newTasks =
      prevKitchenTask?.tasks?.length > 0
        ? [...prevKitchenTask?.tasks, ...tasks]
        : tasks;

    const mergedTasks = prevKitchenTask ? mergeRecipes(newTasks) : newTasks;

    try {
      const code = generateCode(mergedTasks, stationSelected.toString());
      navigate(`/cooking-start/${recipe_id}`, {
        state: { tasks, recipe: data?.getRecipeActions, code },
      });
    } catch (err) {
      console.error("An error occurred:", err);
    }
  }, [
    isReadyToCook,
    prevKitchenTask,
    stationSelected,
    tasks,
    data,
    navigate,
    recipe_id,
    showNotification,
  ]);

  const handleCookLater = useCallback(() => {
    setOpenScheduleModal(true);
  }, []);

  const handleScheduleClose = useCallback(() => {
    setOpenScheduleModal(false);
  }, []);

  const updateIngredientQuantities = useCallback(
    (newServingCount) => {
      // Define task types that should NOT be affected by serving size
      const operationBasedTypes = [
        "choose_stir",
        "choose_seconds",
        "heat_on",
        "heat_off",
        "open_lid",
        "close_lid",
        "chimney_on",
        "chimney_off",
        "release_steam",
      ];

      const updatedTasks = tasks.map((task) => {
        // Only apply serving adjustment to ingredient-based tasks
        let newQuantity = task.quantity;
        if (!operationBasedTypes.includes(task.type)) {
          newQuantity = parseFloat(
            ((task.quantity / servingCount) * newServingCount).toFixed(2)
          );
          console.log(
            `🔧 Legacy serving adjustment: ${task.name} quantity ${task.quantity} → ${newQuantity} (${servingCount} → ${newServingCount} servings)`
          );
        } else {
          console.log(
            `🔧 Skipping legacy serving adjustment for operation: ${task.name} (${task.type})`
          );
        }

        return {
          ...task,
          quantity: newQuantity,
        };
      });
      setTasks(updatedTasks);
      setServingCount(newServingCount);
    },
    [tasks, servingCount]
  );
  // console.log("tasks", tasks);
  if (loading) return <LoadingPage />;
  if (error)
    return (
      <ErrorPage
        message={"Sorry! your requested recipe not found in our database"}
      />
    );

  return (
    <>
      <BreadCrumb
        links={[
          { label: "Dashboard", href: "/dashboard" },
          { label: "Recipes", href: "/recipes" },
          {
            label: data?.getRecipeActions?.recipe_name,
            href: `/recipe/${recipe_id}`,
          },
        ]}
        currentPage={`Cook ${data?.getRecipeActions?.recipe_name}` || "Recipe"}
      />
      {notallowedAlert && (
        <NoPermissionDialog
          message={"Please ask admin to allow cooking permission."}
          notallowedAlert={notallowedAlert}
          handleNotAllowedAlertClose={handleNotAllowedAlertClose}
        />
      )}

      {preloader ? (
        <Preloader />
      ) : (
        <Box
          sx={{
            background: theme.palette.background.default,
            p: { xs: 1, md: 2 },
            pt: { xs: 2 },
            minHeight: "100vh",
          }}
        >
          <Stack spacing={4} mt={2}>
            <Card
              elevation={3}
              sx={{
                padding: theme.spacing(1),
                borderRadius: theme.shape.borderRadius,
                backgroundColor: theme.palette.background.paper,
              }}
            >
              <CardContent sx={{ p: { xs: 0 } }}>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: "bold",
                    color: theme.palette.primary.main,
                    mb: 2,
                  }}
                >
                  Ingredients
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Box
                      sx={{
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: theme.shape.borderRadius,
                        padding: theme.spacing(2),
                        backgroundColor: theme.palette.background.default,
                        boxShadow: theme.shadows[2],
                        // position: "sticky",
                        // width: "100%",
                        overflowX: "hidden",
                        // top: 0,

                        overflowY: "auto",
                        "@media screen and (max-width: 767px)": {
                          padding: 0,
                        },
                      }}
                    >
                      {tasks
                        ?.filter((task) =>
                          Object.keys(ingredientTasksMap).includes(task.name)
                        )
                        .map((task) => (
                          <TaskRow
                            key={task.id}
                            tasks={tasks}
                            task={task}
                            onManualEntry={handleManualEntry}
                            onTaskSwap={setTasks}
                            totaltasks={tasks}
                            inventoryData={inventoryData}
                            matchInventoryWithTasks={matchInventoryWithTasks}
                          />
                        ))}
                    </Box>
                  </Grid>

                  <Grid
                    item
                    xs={12}
                    md={6}
                    spacing={3}
                    sx={{ position: "relative" }}
                  >
                    {/* ANOTHER RECIPE TASKS CARD START */}
                    {prevKitchenTask && (
                      <Box
                        sx={{
                          border: `1px solid ${theme.palette.divider}`,
                          borderRadius: theme.shape.borderRadius,
                          padding: theme.spacing(2),
                          backgroundColor: theme.palette.background.default,
                          boxShadow: theme.shadows[2],
                          position: "sticky",
                          // width: "100%",
                          overflowX: "hidden",
                          // top: 0,
                          height: "auto",
                          // overflowY: "auto",
                          marginBottom: "20px",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            gap: 2,
                            justifyContent: "space-between",
                            alignItems: "center",
                          }}
                        >
                          <Typography
                            variant="h6"
                            sx={{
                              color: theme.palette.primary.main,
                            }}
                          >
                            Previous Added Recipe
                          </Typography>
                          <Box
                            title="remove recipe"
                            sx={{
                              display: "flex",

                              boxSizing: "border-box",
                              color: "red",
                              cursor: "pointer",
                              borderRadius: "4px",
                              "&:hover": {
                                background: "red",
                                color: "white",
                              },
                            }}
                            onClick={removePreKitechenTasks}
                          >
                            <DeleteForeverIcon />
                          </Box>
                        </Box>
                        <Box
                          sx={{
                            display: "flex",
                            gap: 2,
                            justifyContent: "space-between",
                          }}
                        >
                          <Box
                            sx={{
                              color: "white",
                              textAlign: "center",
                            }}
                          >
                            {prevKitchenTask?.recipe?.recipe_category && (
                              <Box
                                sx={{
                                  position: "absolute",
                                  top: "60px",
                                  right: -5,
                                  padding: "8px 0px 0px 8px",
                                  zIndex: 1,
                                }}
                              >
                                {prevKitchenTask?.recipe?.recipe_category
                                  ?.split(",")
                                  ?.map((category, index) => (
                                    <Typography
                                      key={index}
                                      component={"span"}
                                      sx={{
                                        m: 0,
                                        mr: 1,
                                        backgroundColor:
                                          theme.palette.primary.main,
                                        color: theme.palette.common.white,
                                        padding: "8px 16px",
                                        borderRadius: "8px 0 0 8px",
                                        boxShadow: theme.shadows[1],
                                      }}
                                    >
                                      {category.trim()}
                                    </Typography>
                                  ))}
                              </Box>
                            )}
                          </Box>
                          <Box
                            sx={{
                              color: "white",
                              textAlign: "center",
                            }}
                          >
                            <Box
                              sx={{
                                position: "absolute",
                                top: "60px",
                                left: 0,
                                display: "flex",
                                gap: 1,
                                padding: theme.spacing(1),
                                zIndex: 1,
                              }}
                            >
                              {prevKitchenTask?.recipe?.recipe_type
                                .split(",")
                                .map((type) => (
                                  <img
                                    key={type.trim()}
                                    src={type.trim() === "Veg" ? Veg : NonVeg}
                                    alt={type.trim()}
                                    width={30}
                                    height={30}
                                  />
                                ))}
                            </Box>
                          </Box>
                        </Box>

                        <Stack spacing={2} sx={{ paddingTop: "50px" }}>
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                            }}
                          >
                            <Typography
                              variant="subtitle1"
                              sx={{
                                color: theme.palette.text.secondary,
                              }}
                            >
                              Recipe Name:
                            </Typography>
                            <Typography
                              variant="h6"
                              sx={{
                                fontWeight: theme.typography.fontWeightBold,
                                color: theme.palette.text.primary,
                              }}
                            >
                              {prevKitchenTask?.recipe?.recipe_name || "N/A"}
                            </Typography>
                          </Box>

                          {/* Cooking Duration */}
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                            }}
                          >
                            <Typography
                              variant="subtitle1"
                              sx={{
                                color: theme.palette.text.secondary,
                              }}
                            >
                              Cooking Duration:
                            </Typography>
                            <Typography
                              variant="h6"
                              sx={{
                                fontWeight: theme.typography.fontWeightBold,
                                color: theme.palette.text.primary,
                              }}
                            >
                              {Math.floor(
                                (parseInt(prevKitchenTask?.recipe?.cook_time) +
                                  parseInt(
                                    prevKitchenTask?.recipe?.prep_time
                                  )) /
                                  60
                              ) || "N/A"}{" "}
                              mins
                            </Typography>
                          </Box>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              p: 2,
                              border: `1px solid ${theme.palette.divider}`,
                              borderRadius: theme.shape.borderRadius,
                              backgroundColor: theme.palette.background.paper,
                            }}
                          >
                            <Box
                              sx={{ display: "flex", flexDirection: "column" }}
                            >
                              <Typography
                                variant="subtitle1"
                                sx={{
                                  color: theme.palette.text.secondary,
                                  mb: 1,
                                }}
                              >
                                Selected NutriPot:
                              </Typography>

                              <img
                                src={nutripot}
                                alt="Nutripot 2"
                                style={{ width: "50px", height: "50px" }}
                              />
                            </Box>

                            <Chip
                              label={
                                prevKitchenTask?.tasks[0]?.cookIn
                                  ? prevKitchenTask?.tasks[0]?.cookIn.toUpperCase()
                                  : "Not Selected"
                              }
                              color={stationSelected ? "success" : "error"}
                              icon={
                                stationSelected ? (
                                  <CheckCircleIcon />
                                ) : (
                                  <ErrorIcon
                                    sx={{
                                      borderRadius: "50%",
                                      animation: `${glowEffect} 5s ease-in-out infinite`,
                                    }}
                                  />
                                )
                              }
                              sx={{
                                fontSize: theme.typography.body1.fontSize,
                                px: 2,
                              }}
                            />
                          </Box>
                        </Stack>
                      </Box>
                    )}
                    {/* ANOTHER RECIPE TASKS CARD ENDS */}

                    <Box
                      sx={{
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: theme.shape.borderRadius,
                        padding: theme.spacing(2),
                        backgroundColor: theme.palette.background.default,
                        boxShadow: theme.shadows[2],
                        position: "sticky",
                        // width: "100%",
                        overflowX: "hidden",
                        top: 0,
                        overflowY: "auto",
                      }}
                    >
                      {/* Category Ribbon */}
                      {data?.getRecipeActions?.recipe_category && (
                        <Box
                          sx={{
                            position: "absolute",
                            top: 0,
                            right: -5,
                            padding: "8px 0px 0px 8px",
                            zIndex: 1,
                          }}
                        >
                          {data?.getRecipeActions?.recipe_category
                            ?.split(",")
                            ?.map((category, index) => (
                              <Typography
                                key={index}
                                component={"span"}
                                sx={{
                                  m: 0,
                                  mr: 1,
                                  backgroundColor: theme.palette.primary.main,
                                  color: theme.palette.common.white,
                                  padding: "8px 16px",
                                  borderRadius: "0 0 0 8px",
                                  boxShadow: theme.shadows[1],
                                }}
                              >
                                {category.trim()}
                              </Typography>
                            ))}
                        </Box>
                      )}

                      <Box
                        sx={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          display: "flex",
                          gap: 1,
                          padding: theme.spacing(1),
                          zIndex: 1,
                        }}
                      >
                        {data?.getRecipeActions?.recipe_type
                          .split(",")
                          .map((type) => (
                            <img
                              key={type.trim()}
                              src={type.trim() === "Veg" ? Veg : NonVeg}
                              alt={type.trim()}
                              width={30}
                              height={30}
                            />
                          ))}
                      </Box>
                      <Stack spacing={2} paddingTop={3}>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                          }}
                        >
                          <Typography
                            variant="subtitle1"
                            sx={{
                              color: theme.palette.text.secondary,
                            }}
                          >
                            Recipe Name:
                          </Typography>
                          <Typography
                            variant="h6"
                            sx={{
                              fontWeight: theme.typography.fontWeightBold,
                              color: theme.palette.text.primary,
                            }}
                          >
                            {data?.getRecipeActions?.recipe_name || "N/A"}
                          </Typography>
                        </Box>

                        {/* Cooking Duration */}
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                          }}
                        >
                          <Typography
                            variant="subtitle1"
                            sx={{
                              color: theme.palette.text.secondary,
                            }}
                          >
                            Cooking Duration:
                          </Typography>
                          <Typography
                            variant="h6"
                            sx={{
                              fontWeight: theme.typography.fontWeightBold,
                              color: theme.palette.text.primary,
                            }}
                          >
                            {parseInt(data?.getRecipeActions?.cook_time) +
                              parseInt(data?.getRecipeActions?.prep_time) >=
                            60
                              ? Math.floor(
                                  (parseInt(data?.getRecipeActions?.cook_time) +
                                    parseInt(
                                      data?.getRecipeActions?.prep_time
                                    )) /
                                    60
                                ) + " mins"
                              : parseInt(data?.getRecipeActions?.cook_time) +
                                parseInt(data?.getRecipeActions?.prep_time) +
                                " seconds"}
                          </Typography>
                        </Box>
                        {/* Serving Duration */}
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                          }}
                        >
                          <Typography
                            variant="subtitle1"
                            sx={{
                              color: theme.palette.text.secondary,
                            }}
                          >
                            Select Serving:
                          </Typography>
                          <FormControl
                            variant="outlined"
                            sx={{ minWidth: 120 }}
                          >
                            <InputLabel id="serving-select-label">
                              Serving
                            </InputLabel>
                            <Select
                              labelId="serving-select-label"
                              value={servingCount}
                              onChange={(e) =>
                                updateIngredientQuantities(e.target.value)
                              }
                              label="Servings"
                            >
                              {servingPersons.map((num) => (
                                <MenuItem key={num.value} value={num.value}>
                                  {num.name}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Box>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            p: 2,
                            border: `1px solid ${theme.palette.divider}`,
                            borderRadius: theme.shape.borderRadius,
                            backgroundColor: theme.palette.background.paper,
                          }}
                        >
                          <Box
                            sx={{ display: "flex", flexDirection: "column" }}
                          >
                            <Typography
                              variant="subtitle1"
                              sx={{
                                color: theme.palette.text.secondary,
                                mb: 1,
                              }}
                            >
                              Select NutriPot:
                            </Typography>
                            <RadioGroup
                              row
                              aria-label="nutripot"
                              name="nutripot-radio-group"
                              value={stationSelected}
                              onChange={(e) =>
                                handleStationSelect(e.target.value)
                              }
                            >
                              <FormControlLabel
                                value="np1"
                                control={<Radio />}
                                disabled={
                                  prevKitchenTask?.tasks?.length &&
                                  prevKitchenTask?.tasks[0]?.cookIn === "np1"
                                }
                                label={
                                  <img
                                    src={nutripot}
                                    alt="Nutripot 1"
                                    style={{ width: "50px", height: "50px" }}
                                  />
                                }
                              />
                              <FormControlLabel
                                value="np2"
                                control={<Radio />}
                                disabled={
                                  prevKitchenTask?.tasks?.length &&
                                  prevKitchenTask?.tasks[0]?.cookIn === "np2"
                                }
                                label={
                                  <img
                                    src={nutripot}
                                    alt="Nutripot 2"
                                    style={{ width: "50px", height: "50px" }}
                                  />
                                }
                              />
                            </RadioGroup>
                          </Box>

                          <Chip
                            label={
                              stationSelected
                                ? stationSelected.toUpperCase()
                                : "Not Selected"
                            }
                            color={stationSelected ? "success" : "error"}
                            icon={
                              stationSelected ? (
                                <CheckCircleIcon />
                              ) : (
                                <ErrorIcon
                                  sx={{
                                    borderRadius: "50%",
                                    animation: `${glowEffect} 5s ease-in-out infinite`,
                                  }}
                                />
                              )
                            }
                            sx={{
                              fontSize: theme.typography.body1.fontSize,
                              px: 2,
                            }}
                          />
                        </Box>

                        {/* Ready to Cook */}
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                          }}
                        >
                          <Typography
                            variant="subtitle1"
                            sx={{
                              color: theme.palette.text.secondary,
                            }}
                          >
                            Ready to Cook:
                          </Typography>
                          <Chip
                            label={isReadyToCook ? "Yes" : "No"}
                            color={isReadyToCook ? "success" : "warning"}
                            icon={
                              isReadyToCook ? (
                                <CheckCircleIcon />
                              ) : (
                                <ErrorIcon />
                              )
                            }
                            sx={{
                              fontSize: theme.typography.body1.fontSize,
                              padding: theme.spacing(0, 2),
                            }}
                          />
                        </Box>
                      </Stack>

                      {isReadyToCook ? (
                        <Stack
                          direction={"row"}
                          mt={2}
                          spacing={2}
                          sx={{
                            "@media screen and (max-width: 585px)": {
                              flexWrap: "wrap",
                              gap: "15px",
                            },
                          }}
                        >
                          <Button
                            variant="contained"
                            sx={{
                              backgroundColor: theme.palette.success.main,
                              color: theme.palette.common.white,
                              fontWeight: "bold",
                              py: 2,
                              width: "100%",
                              mt: 2,
                              "@media screen and (max-width: 585px)": {
                                flex: 1,
                              },
                            }}
                            onClick={handleCook}
                          >
                            Cook Now
                          </Button>
                          <Button
                            variant="outlined"
                            sx={{
                              py: 2,
                              width: "100%",
                              mt: 2,
                              "@media screen and (max-width: 585px)": {
                                flex: 1,
                              },
                            }}
                            onClick={handleCookLater}
                          >
                            Cook Later
                          </Button>
                          {!prevKitchenTask && (
                            <Button
                              variant="outlined"
                              sx={{
                                py: 2,
                                width: "100%",
                                mt: 2,
                                "@media screen and (max-width: 585px)": {
                                  flex: "1 100%!important",
                                  margin: "0!important",
                                },
                              }}
                              onClick={handleAddAnotherRecipe}
                            >
                              Add Another Recipe
                            </Button>
                          )}
                        </Stack>
                      ) : (
                        <Button
                          variant="contained"
                          disabled
                          sx={{
                            py: 2,
                            width: "100%",
                            mt: 2,
                          }}
                        >
                          {loading ? "Loading..." : "Ready Ingredients Missing"}
                        </Button>
                      )}
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Stack>
        </Box>
      )}

      {/* Schedule Modal */}
      <CookLaterDialog
        openScheduleModal={openScheduleModal}
        handleScheduleClose={handleScheduleClose}
        scheduledDate={scheduledDate}
        setScheduledDate={setScheduledDate}
        cook_in={stationSelected}
        tasks={tasks}
        code={() => generateCode(tasks, stationSelected?.toString())}
        recipe_id={recipe_id}
        recipe_name={data?.getRecipeActions?.recipe_name || "N/A"}
      />
    </>
  );
}

const GET_RECIPE_ACTIONS_QUERY = gql`
  query GetRecipeActions($recipeId: ID!) {
    getRecipeActions(id: $recipeId) {
      id
      recipe_tasks
      recipe_name
      recipe_type
      recipe_category
      recipe_cooking_duration
      serving
      cook_time
      prep_time
    }
  }
`;
const glowEffect = keyframes`
  0% {
    box-shadow: 0 0 5px rgba(255, 255, 255, 0.2);
  }
  50% {
    box-shadow: 0 0 20px rgba(255, 255, 255, 1);
  }
  100% {
    box-shadow: 0 0 5px rgba(255, 255, 255, 0.2);
  }
`;
